/**
 * "Whole card" in the editor (2026-10-10): the paper the card is printed on -
 * a colour, a pattern in a colour of its own, or the person's own words
 * written all over it - and one look for every picture at once (photo,
 * cartoon, or black and white for printing with black ink only). It is one
 * section of the editor's tool palette (palette.tsx), which gives it its title.
 */
import type { PaperStyle } from '@foldmycard/scene';
import { DEFAULT_PAPER, INK_COLOURS, PAPER_COLOURS, PAPER_PATTERNS, PaperSwatch } from './paper.tsx';
import { t, type StringKey } from './strings.ts';

export type Look = 'photo' | 'cartoon' | 'bw';

interface Props {
  paper: PaperStyle | undefined;
  onPaper: (p: PaperStyle | undefined) => void;
  /** What the words default to: the card's title, say. */
  suggestWords: string;
  /** Set every picture's look; `busy` while cartoons are being drawn. */
  onAllLooks: (look: Look) => void;
  busy: string | null;
}

export function CardStyle({ paper, onPaper, suggestWords, onAllLooks, busy }: Props) {
  const p = paper ?? DEFAULT_PAPER;
  const set = (patch: Partial<PaperStyle>) => {
    const next = { ...p, ...patch };
    if (next.pattern === 'words' && !next.words) next.words = suggestWords || 'Happy Birthday';
    // plain white is no paper at all
    onPaper(next.pattern === 'plain' && next.color.toLowerCase() === '#ffffff' ? undefined : next);
  };
  return (
    <div className="card-style">
      <h4>{t('paper')}</h4>
      <div className="swatch-row" role="group" aria-label={t('paperPattern')}>
        {PAPER_PATTERNS.map((k) => (
          <button key={k} className="swatch-button" aria-pressed={p.pattern === k} title={t(`pattern_${k}` as StringKey)}
            onClick={() => set({ pattern: k })}>
            <PaperSwatch paper={{ ...p, pattern: k, words: p.words || suggestWords || 'Happy Birthday' }} />
            <span className="small">{t(`pattern_${k}` as StringKey)}</span>
          </button>
        ))}
      </div>

      <div className="colour-row">
        <span className="small">{t('paperColour')}</span>
        <span className="swatches" role="group" aria-label={t('paperColour')}>
          {PAPER_COLOURS.map((c) => (
            <button key={c} className="swatch" style={{ background: c }} aria-pressed={p.color.toLowerCase() === c}
              aria-label={c} onClick={() => set({ color: c })} />
          ))}
          <input type="color" value={p.color} aria-label={t('otherColour')} onChange={(e) => set({ color: e.target.value })} />
        </span>
      </div>

      {p.pattern !== 'plain' && (
        <div className="colour-row">
          <span className="small">{t('patternColour')}</span>
          <span className="swatches" role="group" aria-label={t('patternColour')}>
            {INK_COLOURS.map((c) => (
              <button key={c} className="swatch" style={{ background: c }} aria-pressed={(p.ink || '').toLowerCase() === c}
                aria-label={c} onClick={() => set({ ink: c })} />
            ))}
            <input type="color" value={p.ink || DEFAULT_PAPER.ink} aria-label={t('otherColour')} onChange={(e) => set({ ink: e.target.value })} />
          </span>
        </div>
      )}

      {p.pattern === 'words' && (
        <label className="stack small">{t('paperWords')}
          <input value={p.words ?? ''} maxLength={80} placeholder="Happy Birthday Eva"
            onChange={(e) => set({ words: e.target.value })} />
        </label>
      )}
      <p className="muted small">{t('paperHint')}</p>

      <h4>{t('allPictures')}</h4>
      <span className="seg" role="group" aria-label={t('allPictures')}>
        <button onClick={() => onAllLooks('photo')} disabled={!!busy}>{t('lookPhoto')}</button>
        <button onClick={() => onAllLooks('cartoon')} disabled={!!busy}>{t('lookCartoon')}</button>
        <button onClick={() => onAllLooks('bw')} disabled={!!busy}>{t('lookBw')}</button>
      </span>
      {busy && <p className="muted small" role="status">{busy}</p>}
      <p className="muted small">{t('allPicturesHint')}</p>
    </div>
  );
}
